import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2Icon, SearchIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { InvoiceStatusBadge } from '../../components/admin/InvoiceStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate } from '../../utils/format';
import { formatKr } from '../../utils/quote';
import { isOverdue } from '../../utils/invoice';
type Filter = 'Alle' | 'Kladder' | 'Sendt' | 'Betalt' | 'Forfalden';
const filters: Filter[] = ['Alle', 'Kladder', 'Sendt', 'Betalt', 'Forfalden'];
export function Invoices() {
  const {
    invoices,
    cases,
    dataLoading
  } = useAdminData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('Alle');
  const rows = useMemo(() => invoices.map(invoice => ({
    invoice,
    overdue: isOverdue(invoice),
    linkedCase: cases.find(item => item.id === invoice.caseId)
  })), [cases, invoices]);
  const summary = useMemo(() => ({
    drafts: rows.filter(({
      invoice
    }) => invoice.status === 'Kladde').length,
    ready: rows.filter(({
      invoice
    }) => invoice.status === 'Klar til afsendelse').length,
    sent: rows.filter(({
      invoice
    }) => invoice.status === 'Sendt').length,
    paid: rows.filter(({
      invoice
    }) => invoice.status === 'Betalt').length,
    overdue: rows.filter(({
      overdue
    }) => overdue).length
  }), [rows]);
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return rows.filter(({
      invoice,
      overdue
    }) => {
      const matchesFilter = filter === 'Alle' || filter === 'Kladder' && invoice.status === 'Kladde' || filter === 'Sendt' && invoice.status === 'Sendt' || filter === 'Betalt' && invoice.status === 'Betalt' || filter === 'Forfalden' && overdue;
      const haystack = `${invoice.invoiceNumber} ${invoice.customerName} ${invoice.reference}`.toLowerCase();
      return matchesFilter && (!term || haystack.includes(term));
    });
  }, [filter, query, rows]);
  const cards = [{
    label: 'Kladder',
    value: summary.drafts
  }, {
    label: 'Klar til afsendelse',
    value: summary.ready
  }, {
    label: 'Sendt',
    value: summary.sent
  }, {
    label: 'Betalt',
    value: summary.paid
  }, {
    label: 'Forfalden',
    value: summary.overdue
  }];
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1028" data-mp="1mg7yjmengq2l">
      <div data-id="element-1029" data-mp="1mj3u5430z2nv">
        <p className="text-sm text-ink-muted" data-id="element-1030" data-mp="1miu117moofdy">{invoices.length} fakturaer i alt</p>
        <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1031" data-mp="1hk0pvluvd0gy">
          Fakturaer
        </h1>
        <p className="mt-2 max-w-xl text-[15px] text-ink-soft" data-id="element-1032" data-mp="1rh5dzr89z60e">
          Fakturaer oprettes fra en sag med knappen «Opret faktura», når arbejdet er udført.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-1033" data-mp="3t80bcni4rd98">
        {cards.map(card => <div key={card.label} className="rounded-card border border-line bg-white p-4" data-id="element-1034" data-mp="28p6ddn2a7exq">
            <p className="font-display text-2xl font-bold tracking-tight text-ink" data-id="element-1035" data-mp="2doso9pun0j3d">{card.value}</p>
            <p className="mt-1 text-sm text-ink-muted" data-id="element-1036" data-mp="1z0endkgs74pg">{card.label}</p>
          </div>)}
      </div>

      <div className="mt-6" data-id="element-1037" data-mp="23xo9aq4yoy8j">
        <label htmlFor="fakturasoegning" className="sr-only" data-id="element-1038" data-mp="23wvbienzdj2j">
          Søg i fakturaer
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-id="element-1039" data-mp="1pxn9le0fnwun">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-1040" data-mp="1u1v1fk26tsx3" />
          <input id="fakturasoegning" value={query} onChange={event => setQuery(event.target.value)} placeholder="Søg på fakturanummer, kunde eller reference" className="w-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint focus:outline-none" data-id="element-1041" data-mp="1g2bugul44nmd" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filtrér fakturaer" data-id="element-1042" data-mp="1fbdbvgcexzvk">
          {filters.map(option => {
          const active = filter === option;
          return <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors duration-200 ease-smooth ${active ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-1043" data-mp="169dxywwp3bwu">
                {option}
              </button>;
        })}
        </div>
      </div>

      {dataLoading && invoices.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-1044" data-mp="8ngjrbpclsqp">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1045" data-mp="8pj87raurdtb" />
          Henter fakturaer …
        </p>}

      <div className="mt-6 rounded-card border border-line bg-white" data-id="element-1046" data-mp="2p93p4q48a3bx">
        <div className="hidden md:block" data-id="element-1047" data-mp="2pbg3ktas1vk8">
          <table className="w-full text-left text-[15px]" data-id="element-1048" data-mp="2u8gbl9r6t8dz">
            <thead data-id="element-1049" data-mp="2u6vhtmcp5th6">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-1050" data-mp="2ya1mipk5vclj">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1051" data-mp="21kkh8bp3bskd">Fakturanummer</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1052" data-mp="26j583kigczw5">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1053" data-mp="26h14l3ogu2we">Dato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1054" data-mp="2ajxiih329au0">Forfaldsdato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1055" data-mp="2fch4a1bwjc9w">Beløb</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1056" data-mp="1iu2qudrqd926">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1057" data-mp="1irq8hzi28wol">Tilknyttet sag</th>
                <th scope="col" className="px-5 py-3 text-right font-medium" data-id="element-1058" data-mp="1mt1qpjylufmr">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1059" data-mp="2l78p14nwu21b">
              {visible.map(({
              invoice,
              overdue,
              linkedCase
            }) => <tr key={invoice.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-1060" data-mp="2zw4vwye8d90c">
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1061" data-mp="2776bfummxurv">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-5 py-3.5 text-ink" data-id="element-1062" data-mp="229ej4zi2w6n5">{invoice.customerName || '—'}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1063" data-mp="2g8xeerpsjni1">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1064" data-mp="3rn17zwsgv9mk">
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1065" data-mp="3nko4zg53tpth">
                    {formatKr(invoice.total)}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-1066" data-mp="3ye327finr9k">
                    <InvoiceStatusBadge status={overdue ? 'Forfalden' : invoice.status} data-id="element-1067" data-mp="3zfieo3kezx3" />
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1068" data-mp="3l8lxolin6785">
                    {linkedCase ? <Link to={`/admin/sager/${linkedCase.id}`} className="font-medium text-accent hover:text-accent-deep" data-id="element-1069" data-mp="3q7p3w9acyjhp">
                        Sag #{linkedCase.number}
                      </Link> : '—'}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-1070" data-mp="cxre34jagnwe">
                    <Link to={`/admin/fakturaer/${invoice.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-1071" data-mp="10oeopxkl9rmn">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-1072" data-mp="2wr9q5pmnm0qz">
          {visible.map(({
          invoice,
          overdue
        }) => <li key={invoice.id} data-id="element-1073" data-mp="2sqzkyf6ored6">
              <Link to={`/admin/fakturaer/${invoice.id}`} className="block px-5 py-4" data-id="element-1074" data-mp="2nrd6y3j19u3b">
                <div className="flex items-start justify-between gap-3" data-id="element-1075" data-mp="2npjxyhky6xs5">
                  <div className="min-w-0" data-id="element-1076" data-mp="3kh41g2rbrh94">
                    <p className="text-xs text-ink-faint" data-id="element-1077" data-mp="3ggbpv2uzd09o">{invoice.invoiceNumber}</p>
                    <p className="mt-1 truncate text-[15px] font-medium text-ink" data-id="element-1078" data-mp="3bgz2mh075r6k">
                      {invoice.customerName || '—'}
                    </p>
                  </div>
                  <InvoiceStatusBadge status={overdue ? 'Forfalden' : invoice.status} data-id="element-1079" data-mp="36jyv01np8gdc" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-1080" data-mp="1zzfuqbbo901o">
                  {formatDate(invoice.invoiceDate)} · forfald {formatDate(invoice.dueDate)} ·{' '}
                  {formatKr(invoice.total)}
                </p>
              </Link>
            </li>)}
        </ul>

        {!dataLoading && visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-ink-muted" data-id="element-1081" data-mp="1uworh212c4o2">
            Ingen fakturaer matcher søgningen.
          </p>}
      </div>
    </div>;
}